import { cn, useFormat } from '@nfctron/nexus-react';

import { ActionIcon, Text } from '@mantine/core';
import { MinusIcon, PlusIcon } from 'lucide-react';

import { type Component } from '@root/lib/types/components';

import { type CounterControlProps } from './CounterControl.types';

/**
 * CounterControl component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const CounterControl: Component<CounterControlProps> = ({
	value,
	onDecrement,
	onIncrement,
	decrementActionProps,
	incrementActionProps,
	className,
	size = 'md',
	isDisabled,
	...props
}) => {
	const format = useFormat();
	return (
		<div
			className={cn([
				'flex items-center justify-between rounded-full border border-gray-200 bg-white px-[6.4px] py-1',
				size === 'md' && 'h-10.5 gap-2.5',
				size === 'sm' && 'h-[38px] gap-1.5',
				className,
			])}
			{...props}
		>
			{/* remove button */}
			<ActionIcon
				variant="filled"
				radius="xl"
				size={size === 'md' ? 32 : 28}
				color="lucky-point"
				disabled={value <= 0}
				onClick={() => onDecrement(value - 1)}
				{...decrementActionProps}
			>
				<MinusIcon size={size === 'md' ? 20 : 16} />
			</ActionIcon>

			{/* carted tickets count */}
			<div className="min-w-6 text-center">
				<Text fw={700} c="lucky-point" className={cn([size === 'sm' && 'text-lg!', size === 'md' && 'text-2xl!', isDisabled && 'text-gray-400!'])}>
					{format.number(value)}
				</Text>
			</div>

			{/* add button */}
			<ActionIcon
				size={size === 'md' ? 32 : 28}
				variant="filled"
				color="lucky-point"
				onClick={() => onIncrement(value + 1)}
				{...incrementActionProps}
			>
				<PlusIcon size={size === 'md' ? 20 : 16} />
			</ActionIcon>
		</div>
	);
};

/**
 * CounterControl component display name
 * @type {ComponentDisplayName}
 */
CounterControl.displayName = 'component/CounterControl';
